import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import type { DashboardStats } from '@/api/types';
import type { DrillDownKind } from './types';

function fmt(n: number | null | undefined) {
  const v = isNaN(Number(n)) ? 0 : (Number(n) ?? 0);
  return '₹' + v.toLocaleString('en-IN');
}

function fmtShort(n: number | null | undefined) {
  const v = isNaN(Number(n)) ? 0 : (Number(n) ?? 0);
  if (v >= 100000) return '₹' + (v / 100000).toFixed(1) + 'L';
  if (v >= 1000) return '₹' + (v / 1000).toFixed(1) + 'k';
  return '₹' + v.toLocaleString('en-IN');
}

interface RevenueSnapshotCardProps {
  stats: DashboardStats;
  revenueData: { month: string; amount: number }[];
  onSelectPendingRent: (kind: DrillDownKind) => void;
  accentHex?: string;
}

export function RevenueSnapshotCard({ stats, revenueData, onSelectPendingRent, accentHex = '#0d9488' }: RevenueSnapshotCardProps) {
  const { t } = useTranslation();

  // revenueData is ordered most-recent-first; take the last 6 months and flip to chronological order for the chart.
  const last6Months = [...revenueData.slice(0, 6)].reverse();

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.revenue')}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-xs text-green-600 font-medium">{t('dashboard.collectedThisMonth')}</p>
          <p className="text-lg font-bold text-green-700 mt-1">{fmtShort(stats.collectedThisMonth)}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-xs text-blue-600 font-medium">{t('dashboard.monthlyTarget')}</p>
          <p className="text-lg font-bold text-blue-700 mt-1">{fmtShort(stats.monthlyTarget)}</p>
        </div>
        <button
          type="button"
          onClick={() => onSelectPendingRent('PENDING_RENT')}
          className="bg-amber-50 rounded-lg p-3 text-center hover:bg-amber-100 transition"
        >
          <p className="text-xs text-amber-600 font-medium">{t('dashboard.outstanding')}</p>
          <p className="text-lg font-bold text-amber-700 mt-1">{fmtShort(stats.pendingAmount)}</p>
        </button>
        <button
          type="button"
          onClick={() => onSelectPendingRent('OVERDUE_RENT')}
          className="bg-red-50 rounded-lg p-3 text-center hover:bg-red-100 transition"
        >
          <p className="text-xs text-red-600 font-medium">{t('dashboard.latePayments')}</p>
          <p className="text-lg font-bold text-red-700 mt-1">{t('dashboard.lateTenantsCount', { count: stats.overdueRent })}</p>
        </button>
      </div>

      {last6Months.length > 0 && (
        <>
          <p className="text-xs font-medium text-gray-400 mb-2">{t('dashboard.last6Months')}</p>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={last6Months}>
              <defs>
                <linearGradient id="revSnapGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={accentHex} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={accentHex} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: number) => [fmt(value), t('dashboard.revenue')]} />
              <Area type="monotone" dataKey="amount" stroke={accentHex} strokeWidth={2} fill="url(#revSnapGrad)" dot={{ r: 3, fill: accentHex }} />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}
